import React, { createContext, useContext } from "react";

// ─────────────────────────────────────────────
// THEME DEFINITIONS
// Each theme is an object (like a box) that holds
// everything a component needs to know about its look.
// ─────────────────────────────────────────────

const knightTheme = {
  id: "knight",
  // Colors
  bgPage:       "#0d1b2e",   // dark navy — the page background
  bgCard:       "#1e3a5f",   // mid navy — cards and panels
  bgCardBorder: "#2d5986",   // lighter navy — card borders
  colorPrimary: "#2563eb",   // bright blue — buttons, highlights
  colorAccent:  "#fbbf24",   // gold — trim, badges, XP bar
  colorSuccess: "#4ade80",   // green — completed, HP bar
  colorText:    "#e2e8f0",   // near-white — main text
  colorTextSoft:"#93b4cc",   // muted blue-white — secondary text
  // Fonts
  fontDisplay:  "'Press Start 2P', monospace",  // pixel font for headings
  fontBody:     "'Nunito', sans-serif",          // round friendly font for body text
  // Character
  heroPrefix:   "Sir",                  // "Sir Adam"
  heroSprite:   "/assets/knight.png",
  sidekickName: "Sprocket",
  sidekickSprite: "/assets/sprocket.png",
  sidekickMessages: [
    "Keep reading, brave knight! ⚔️",
    "Every page makes you stronger!",
    "Quest complete! Log your session!",
    "You're on a roll — don't stop now!",
    "Sir, your book awaits! 📖",
    "BEEP BOOP... reading levels: MAXIMUM. ⚙️",
    "CALCULATING... you are 100% awesome. 🤖",
    "WARNING: this book is dangerously good. 📖",
    "PROCESSING... quest progress looking strong! ⚔️",
    "UNIT DETECTED reading. ACHIEVEMENT UNLOCKED. 🔧",
  ],
  // UI labels
  bookLabel:    "Quest",       // "My Quests" instead of "My Books"
  sessionLabel: "Battle",      // "Log a Battle" instead of "Log a Session"
  addBookLabel: "+ New Quest",
  icon: "⚔️",
};

const princessTheme = {
  id: "princess",
  bgPage:       "#1a0628",
  bgCard:       "#3b0d54",
  bgCardBorder: "#7b2d6e",
  colorPrimary: "#9333ea",
  colorAccent:  "#fbbf24",
  colorSuccess: "#4ade80",
  colorText:    "#f3e8ff",
  colorTextSoft:"#c89ee0",
  fontDisplay:  "'Press Start 2P', monospace",
  fontBody:     "'Nunito', sans-serif",
  heroPrefix:   "Lady",
  heroSprite:   "/assets/princess.png",
  sidekickName: "Muphy",
  sidekickSprite: "/assets/muphy.png",
  sidekickMessages: [
    "A true princess never stops learning! ✨",
    "Your story is just beginning!",
    "Muphy believes in you! 🐉",
    "Keep reading — magic awaits!",
    "Every chapter is a new adventure!",
    "By dragonfire, you're doing amazingly! 🔥",
    "Muphy roars with pride for you! 🐉",
    "Even dragons love a good story!",
    "Your reading powers grow stronger! ✨",
    "I'd breathe fire but books are better! 📖",
  ],
  bookLabel:    "Scroll",
  sessionLabel: "Chapter",
  addBookLabel: "+ New Scroll",
  icon: "✨",
};

const neutralBoyTheme = {
  id: "neutral-boy",
  bgPage:       "#fdf6e3",
  bgCard:       "#ffffff",
  bgCardBorder: "#d4a847",
  colorPrimary: "#2e7d32",   // chalkboard green
  colorAccent:  "#d4a847",   // amber
  colorSuccess: "#4ade80",
  colorText:    "#3d2b00",   // dark warm brown
  colorTextSoft:"#7a5c1e",
  fontDisplay:  "'Nunito', sans-serif",
  fontBody:     "'Nunito', sans-serif",
  heroPrefix:   "",           // no prefix — just the child's name
  heroSprite:   "/assets/mr.png",
  sidekickName: "Ruffus",
  sidekickSprite: "/assets/ruffus.png",
  sidekickMessages: [
    "Great work today! 🐶",
    "Ruffus says keep it up!",
    "Mr. Clarke is proud of you!",
    "Log your reading and earn a star! ⭐",
    "Books are your superpower!",
    "WOOF! You're doing SO great today! 🐶",
    "Ruffus thinks you're the best reader EVER!",
    "Tail is wagging — keep reading! 🐾",
    "Good boy/girl! Now read that book! 📚",
    "BARK BARK — that means awesome job! 🦴",
  ],
  bookLabel:    "Book",
  sessionLabel: "Session",
  addBookLabel: "+ Add Book",
  icon: "📚",
};

const neutralGirlTheme = {
  id: "neutral-girl",
  bgPage:       "#fdf6e3",
  bgCard:       "#ffffff",
  bgCardBorder: "#d4a847",
  colorPrimary: "#2e7d32",
  colorAccent:  "#d4a847",
  colorSuccess: "#4ade80",
  colorText:    "#3d2b00",
  colorTextSoft:"#7a5c1e",
  fontDisplay:  "'Nunito', sans-serif",
  fontBody:     "'Nunito', sans-serif",
  heroPrefix:   "",
  heroSprite:   "/assets/mrs.png",
  sidekickName: "Meowtha",
  sidekickSprite: "/assets/meowtha.png",
  sidekickMessages: [
    "Purrfect reading! 🐱",
    "Meowtha loves a good story!",
    "Mrs. Clarke is cheering for you!",
    "You're doing amazing — keep going! ⭐",
    "Books open every door!",
    "Purrfectly done, young scholar... 🐱",
    "Meowtha approves of your reading choices.",
    "A wise cat knows... books are magic. ✨",
    "You may pet me later. Read first. 📖",
    "Meowtha is watching... and very impressed. 🌟",
  ],
  bookLabel:    "Book",
  sessionLabel: "Session",
  addBookLabel: "+ Add Book",
  icon: "📚",
};

// ─────────────────────────────────────────────
// THEME MAP
// A lookup table so we can go from a string like
// "knight" to the full theme object instantly.
// ─────────────────────────────────────────────
export const THEMES = {
  knight:        knightTheme,
  princess:      princessTheme,
  "neutral-boy": neutralBoyTheme,
  "neutral-girl": neutralGirlTheme,
};

// ─────────────────────────────────────────────
// THE CONTEXT
// This is the "vending machine" itself.
// We create it with knightTheme as the default
// so nothing crashes if it's used before a child logs in.
// ─────────────────────────────────────────────
export const ThemeContext = createContext(knightTheme);

// ─────────────────────────────────────────────
// THE HOOK
// Any component can call: const theme = useTheme();
// and immediately get back the active theme object.
// ─────────────────────────────────────────────
export function useTheme() {
  return useContext(ThemeContext);
}